import { Component, effect, inject, input, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, switchMap } from 'rxjs/operators';
import { RegistrierungService } from '../../services/registrierung.service';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatIcon } from '@angular/material/icon';
import { MatError, MatFormField, MatInput } from '@angular/material/input';
import { LastschriftMTO } from '../../generated-model/lastschriftMTO';
import { TranslatedNotificationService } from '../../services/translated-notification.service';
import { RegistrierungComponent } from '../registrierung.component';
import { VertragszustimmungMTO } from '../../generated-model/vertragszustimmungMTO';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-lastschrift',
  imports: [MatFormField, MatIcon, MatInput, ReactiveFormsModule, MatFormField, MatCheckbox, MatError, TranslatePipe],
  templateUrl: './lastschrift.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './lastschrift.component.scss',
})
export class LastschriftComponent {
  private readonly registrierungService = inject(RegistrierungService);
  protected msg = inject(TranslatedNotificationService);
  registrierungComponent = inject(RegistrierungComponent);

  // Signal inputs and state
  formGroupLastschrift = input<FormGroup>({} as FormGroup);
  ibanOriginal = signal<string | undefined>('');

  constructor() {
    // Setup auto-save effect on form value changes
    effect(() => {
      const fg = this.formGroupLastschrift();
      if (fg?.valueChanges) {
        fg.valueChanges
          .pipe(
            debounceTime(200),
            switchMap((value) => {
              return this.updateLastschrift(value, fg);
            })
          )
          .subscribe((result) => {
            if (result === false) {
              console.log('formular failure: autosave not executed');
            }
          });
      }
    });
  }

  private updateLastschrift(value: any, fg: FormGroup) {
    const { kontoinhaber, iban, lastschrift_erlaubt, einverstanden_vertrag, berechtigung_stellvertretung } = value;
    const bic = fg.get('bic')?.value;
    const bankname = fg.get('kreditinstitut')?.value;

    const lastschrift: LastschriftMTO = { kontoinhaber, iban, bic, bankname, lastschrift_erlaubt };
    const vertragzustimmung: VertragszustimmungMTO = {} as VertragszustimmungMTO;
    vertragzustimmung.vertragsabschluss = einverstanden_vertrag;
    vertragzustimmung.vertragsbestimmungen = einverstanden_vertrag;

    if (iban && iban !== this.ibanOriginal()) {
      this.ibanOriginal.set(iban);
      if (iban.startsWith('DE') || iban.startsWith('de')) {
        return this.checkLastschriftdaten(lastschrift);
      } else {
        this.msg.showWarningMessage('registrierung.lastschrift.keine_iban_ausland');
        fg.patchValue({ bic: '', kreditinstitut: '' });
      }
    }

    if (this.registrierungService.registrierungsdaten.benutzerdaten) {
      this.registrierungService.registrierungsdaten.benutzerdaten.berechtigung_stellvertretung =
        berechtigung_stellvertretung;
    }
    this.registrierungService.registrierungsdaten.lastschrift = lastschrift;
    this.registrierungService.registrierungsdaten.vertragszustimmung = vertragzustimmung;

    this.setzeLastSchriftValidierStatus();
    return this.registrierungService.saveRegistrierungsdatenToLocalStorage();
  }

  private checkLastschriftdaten(lastschrift: LastschriftMTO) {
    return this.registrierungService.checkLastschriftdaten(lastschrift).pipe(
      switchMap((response) => {
        const { success, bic, bankname, message } = response;
        const fg = this.formGroupLastschrift();

        fg.patchValue(success ? { bic, kreditinstitut: bankname } : { bic: '', kreditinstitut: '' });

        if (success) {
          Object.assign(lastschrift, { bic, bankname });
        } else {
          this.msg.showErrorMessage(message);
        }

        this.registrierungService.registrierungsdaten.lastschrift = lastschrift;
        return this.registrierungService.saveRegistrierungsdatenToLocalStorage();
      })
    );
  }

  isStellvertreterRegelungOK(): boolean {
    return this.formGroupLastschrift().get('berechtigung_stellvertretung')?.value === true;
  }

  isEinverstandenVertrag(): boolean {
    return this.formGroupLastschrift().get('einverstanden_vertrag')?.value === true;
  }

  private isLastschriftdatenVollstaendig(): boolean {
    const fg = this.formGroupLastschrift();

    const hasKontoinhaber = Boolean(fg.get('kontoinhaber')?.value);
    const isIbanValid = fg.get('iban')?.valid === true;
    const hasKreditinstitut = Boolean(fg.get('kreditinstitut')?.value);

    return hasKontoinhaber && isIbanValid && hasKreditinstitut;
  }

  private setzeLastSchriftValidierStatus() {
    this.registrierungComponent.abschlussRegistrierungOK.set(
      this.isEinverstandenVertrag() && this.isStellvertreterRegelungOK() && this.isLastschriftdatenVollstaendig()
    );
  }
}
